import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowLeft, ExternalLink, Share2, Sparkles, Feather, Wrench, Image as ImageIcon,
  ListMusic, BookOpen, Eye, Download,
} from "lucide-react";
import { STREAMING_LINKS, formatReleaseDate, type StreamingPlatform, type Release, type TrackListing, type ReleaseCredit } from "@/lib/content";
import { findSongDb, getReleases } from "@/lib/music.functions";
import { ReleaseCover } from "@/components/site/ReleaseCover";
import { PlayButton } from "@/components/site/PlayButton";
import { FavoriteButton } from "@/components/site/FavoriteButton";
import { RelatedRail } from "@/components/site/RelatedRail";
import { SongComments } from "@/components/site/SongComments";
import { PreSaveCTA } from "@/components/site/PreSaveCTA";
import { getSongEssay } from "@/lib/song-essays";
import { absoluteUrl, SITE_URL } from "@/lib/site";
import { breadcrumbs, ldScript, musicRecordingNode } from "@/lib/schema";

import type { PlayerTrack } from "@/lib/player/PlayerContext";

/**
 * Superseded Diamond Sutra song identities. The owner replaced these tracks
 * with Create and Elevate; the old URLs were already indexable, so they are
 * permanently redirected instead of left as duplicate/dead pages.
 */
const LEGACY_SONG_SLUGS: Record<string, Record<string, string>> = {
  "the-diamond-sutra": {
    "the-divine": "create",
    "heartbeat-low": "elevate",
  },
};

export const Route = createFileRoute("/music/$slug/song/$song")({
  beforeLoad: ({ params }) => {
    const target = LEGACY_SONG_SLUGS[params.slug]?.[params.song];
    if (target) {
      throw redirect({
        to: "/music/$slug/song/$song",
        params: { slug: params.slug, song: target },
        statusCode: 301,
      });
    }
  },
  loader: async ({ params }) => {
    const found = await findSongDb({ data: { releaseSlug: params.slug, songSlug: params.song } });
    if (!found) throw notFound();
    const releases = await getReleases();
    return { ...found, releases };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">This song is temporarily unavailable.</p>
      <Link to="/music" className="mt-4 inline-block text-gold-luminous">← Return to the archive</Link>
    </div>
  ),
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Song not found · Buddha KA" }, { name: "robots", content: "noindex" }] };
    }
    const { release, track, index } = loaderData;
    const canonical = absoluteUrl(`/music/${release.slug}/song/${track.slug}`);
    const releaseUrl = absoluteUrl(`/music/${release.slug}`);
    const essay = getSongEssay(release.slug, track.slug);
    const title = essay?.seoTitle ?? `${track.title} · ${release.title} · Buddha KA`;
    const description =
      essay?.seoDescription ??
      track.meaning ??
      track.story ??
      `“${track.title}” by Buddha KA, from the ${release.type.toLowerCase()} ${release.title}${
        release.frequency ? ` · ${release.frequency}` : ""
      }.`;
    const ogImage = release.coverUrl ? absoluteUrl(release.coverUrl) : null;


    // "3:42" → "PT3M42S" for schema.org duration.
    const isoDuration = (() => {
      const raw = track.duration;
      if (!raw) return null;
      const m = /^(\d+):([0-5]\d)$/.exec(raw.trim());
      return m ? `PT${Number(m[1])}M${Number(m[2])}S` : null;
    })();

    const ld: Record<string, unknown> = {
      "@context": "https://schema.org",
      ...musicRecordingNode({
        releaseSlug: release.slug,
        releaseTitle: release.title,
        songSlug: track.slug ?? track.title,
        name: track.title,
        position: index + 1,
        description,
        duration: track.duration ?? undefined,
        audioUrl: track.audioUrl ?? undefined,
        hasLyrics: Boolean(track.lyrics?.length),
        datePublished: release.releaseDate ?? (release.year ? String(release.year) : undefined),
        image: release.coverUrl ?? undefined,
      }),
    };

    const breadcrumb = breadcrumbs([
      ["Music", "/music"],
      [release.title, `/music/${release.slug}`],
      [track.title, `/music/${release.slug}/song/${track.slug}`],
    ]);


    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "music.song" },
        { property: "og:url", content: canonical },
        { property: "og:site_name", content: "Buddha KA · KAfrequency" },
        { property: "music:album", content: releaseUrl },
        { property: "music:musician", content: `${SITE_URL}/about` },
        ...(isoDuration && track.duration
          ? [{ property: "music:duration", content: String(Number(track.duration.split(":")[0]) * 60 + Number(track.duration.split(":")[1])) }]
          : []),
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(ogImage
          ? [
              { property: "og:image", content: ogImage },
              { property: "og:image:alt", content: `${release.title} cover art` },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: ogImage },
            ]
          : [{ name: "twitter:card", content: "summary" }]),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(ld) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
      ],
    };
  },
  component: SongHub,
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-gold">Song not found.</p>
      <Link to="/music" className="mt-4 inline-block text-gold-luminous">← Return to the archive</Link>
    </div>
  ),
});

function SongHub() {
  const data = Route.useLoaderData() as { release: Release; track: TrackListing; releases: Release[] };
  const { release, track, releases } = data;
  const [copied, setCopied] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const essay = getSongEssay(release.slug, track.slug);
  const isPreRelease = release.status !== "released";
  const releaseDateLabel = formatReleaseDate(release);
  const trackSlugs = new Set((release.tracks ?? []).map((t) => t.slug));
  const companionLinks = [...(essay?.companion ? [essay.companion] : []), ...(essay?.companions ?? [])]
    .filter((c, i, arr) => trackSlugs.has(c.songSlug) && arr.findIndex((x) => x.songSlug === c.songSlug) === i)
    .slice(0, 3);

  const platforms: StreamingPlatform[] = ["Spotify", "Apple Music", "YouTube", "SoundCloud", "Bandcamp"];
  // Pre-release songs never fall back to generic artist-profile links: only
  // verified per-song / per-release destinations are ever surfaced.
  const availableLinks = platforms
    .map((p) => ({
      platform: p,
      url: track.links?.[p] ?? release.links?.[p] ?? (isPreRelease ? undefined : STREAMING_LINKS[p]),
    }))
    .filter((x): x is { platform: StreamingPlatform; url: string } => Boolean(x.url));

  const playerTrack: PlayerTrack | null = track.audioUrl
    ? {
        id: `${release.slug}:${track.slug ?? track.title}`,
        title: track.title,
        subtitle: release.title,
        audioUrl: track.audioUrl,
        coverUrl: release.coverUrl,
        href: `/music/${release.slug}/song/${track.slug}`,
      }
    : null;

  // Sibling songs from the same release. Every sibling is linked so each song
  // page is one hop from the whole release — a complete crawl mesh.
  const siblings = (release.tracks ?? []).filter((t: TrackListing) => t.slug && t.slug !== track.slug);

  // Playlist suggestions: sibling releases sharing mood
  const suggestions = releases
    .filter((r) => r.slug !== release.slug && r.mood.some((m) => release.mood.includes(m)))
    .slice(0, 3);

  async function share() {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    const nav = window.navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      try { await nav.share({ title: track.title, text: release.title, url }); return; } catch { /* fall through */ }
    }
    try { await nav.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* noop */ }
  }

  return (
    <article className="pb-24">
      {/* HERO */}
      <section className="relative overflow-hidden border-b hairline">
        <div className="absolute inset-0 -z-10 halo animate-halo" aria-hidden />
        {release.coverUrl && (
          <div
            className="absolute inset-0 -z-20 opacity-25 blur-3xl"
            style={{ backgroundImage: `url(${release.coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" }}
            aria-hidden
          />
        )}
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-24">
          <Link
            to="/music/$slug"
            params={{ slug: release.slug }}
            className="mb-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold"
          >
            <ArrowLeft size={14} /> {release.title}
          </Link>
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-16">
            <ReleaseCover
              coverUrl={release.coverUrl}
              label={`${release.title} cover art`}
              className="mx-auto w-full max-w-xs shadow-gold sm:max-w-md"
              alt={`${release.title} cover art`}
              loading="eager"
            />
            <div className="flex flex-col justify-center">
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold sm:text-xs">
                Song · {release.title}
                {track.duration ? ` · ${track.duration}` : ""}
              </p>
              <h1 className="mt-3 font-serif text-4xl leading-tight sm:mt-4 sm:text-5xl md:text-6xl lg:text-7xl">
                {track.title}
              </h1>

              <p className="mt-5 text-base text-ivory/85 sm:text-lg">
                <Link
                  to="/artist/buddha-ka"
                  className="font-serif text-gold-luminous underline decoration-gold/40 underline-offset-4 hover:decoration-gold"
                >
                  Buddha KA
                </Link>
                <span className="text-muted-foreground"> · From </span>
                <Link
                  to="/music/$slug"
                  params={{ slug: release.slug }}
                  className="font-serif text-gold-luminous underline decoration-gold/40 underline-offset-4 hover:decoration-gold"
                >
                  {release.title}
                </Link>
                {releaseDateLabel && (
                  <span className="text-muted-foreground"> · {releaseDateLabel}</span>
                )}
              </p>

              {essay?.standfirst && (
                <p className="mt-5 max-w-xl font-serif text-lg italic text-ivory/85">{essay.standfirst}</p>
              )}
              {track.meaning && (
                <p className="mt-6 max-w-xl font-serif text-lg italic text-ivory/85">{track.meaning}</p>
              )}

              {isPreRelease && release.preSaveUrl && (
                <div className="mt-8">
                  <a
                    href={release.preSaveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
                  >
                    Pre-save {release.title} <ExternalLink size={14} aria-hidden />
                  </a>
                </div>
              )}


              <div className="mt-8 flex flex-wrap items-center gap-3">
                {playerTrack && <PlayButton track={playerTrack} label="Play" />}
                {track.slug && <FavoriteButton kind="song" slug={track.slug} />}
                <button
                  onClick={share}
                  className="inline-flex min-h-11 items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold"
                  aria-label="Share this song"
                >
                  <Share2 size={12} /> {copied ? "Link copied" : "Share"}
                </button>
              </div>

              {availableLinks.length > 0 && (
                <div className="mt-8" aria-label="Listen on">
                  <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Listen on</p>
                  <div className="flex flex-wrap gap-2">
                    {availableLinks.map(({ platform, url }) => (
                      <a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-11 items-center gap-1 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
                      >
                        {platform} <ExternalLink size={10} aria-hidden />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <div className="mx-auto grid max-w-7xl gap-14 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[2fr_1fr] lg:gap-20">
        <div className="space-y-16">
          {essay && (
            <>
              {essay.sections.map((s) => (
                <SongSection key={s.title} icon={Feather} eyebrow={s.eyebrow} title={s.title}>
                  <div className="space-y-5">
                    {s.paragraphs.map((p, i) => (
                      <p key={i} className="text-base leading-relaxed text-ivory/85 sm:text-lg">
                        {p}
                      </p>
                    ))}
                  </div>
                </SongSection>
              ))}

              {companionLinks.length > 0 && (
                <div className="rounded-2xl border hairline bg-obsidian/40 p-6">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Continue the inquiry</p>
                  <ul className="mt-2 space-y-4">
                    {companionLinks.map((c) => (
                      <li key={c.songSlug}>
                        <Link
                          to="/music/$slug/song/$song"
                          params={{ slug: release.slug, song: c.songSlug }}
                          className="inline-flex min-h-11 items-center font-serif text-xl text-ivory hover:text-gold-luminous"
                        >
                          {c.title} →
                        </Link>
                        <p className="mt-1 text-sm text-muted-foreground">{c.reason}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {track.lyrics && track.lyrics.length > 0 && (
            <SongSection icon={BookOpen} eyebrow="The Verses" title="Lyrics">
              <div className="space-y-3 rounded-2xl border hairline bg-obsidian/40 p-6 sm:p-8">
                {track.lyrics.map((line, i) => (
                  <p key={i} className="font-serif text-base italic leading-relaxed text-ivory/90 sm:text-lg">
                    {line || <span className="opacity-40">·</span>}
                  </p>
                ))}
              </div>
            </SongSection>
          )}

          {track.story && (
            <SongSection icon={Feather} eyebrow="Behind the Transmission" title="The Story">
              <p className="font-serif text-lg leading-relaxed text-ivory/85 sm:text-xl">{track.story}</p>
            </SongSection>
          )}

          {track.hiddenSymbols && track.hiddenSymbols.length > 0 && (
            <SongSection icon={Eye} eyebrow="Encoded Layer" title="Hidden Symbols">
              <ul className="grid gap-4 sm:grid-cols-2">
                {track.hiddenSymbols.map((s, i) => (
                  <li key={i} className="rounded-2xl border hairline bg-gradient-to-br from-obsidian/70 to-obsidian/30 p-5">
                    <p className="font-serif text-2xl text-gold-luminous">{s.symbol}</p>
                    <p className="mt-2 text-sm text-ivory/80">{s.meaning}</p>
                  </li>
                ))}
              </ul>
            </SongSection>
          )}

          {track.productionNotes && track.productionNotes.length > 0 && (
            <SongSection icon={Wrench} eyebrow="From the Board" title="Production Notes">
              <ul className="space-y-2 text-sm text-ivory/80">
                {track.productionNotes.map((n, i) => (
                  <li key={i} className="border-l-2 border-gold/40 pl-4">{n}</li>
                ))}
              </ul>
            </SongSection>
          )}

          {track.credits && track.credits.length > 0 && (
            <SongSection icon={Sparkles} eyebrow="Hands on the Work" title="Credits">
              <ul className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                {track.credits.map((c, i) => (
                  <li key={i} className="flex justify-between gap-4 border-b hairline pb-2">
                    <span className="uppercase tracking-widest text-[10px] text-gold">{c.role}</span>
                    <span className="text-ivory/80">{c.name}</span>
                  </li>
                ))}
              </ul>
            </SongSection>
          )}

          {track.gallery && track.gallery.length > 0 && (
            <SongSection icon={ImageIcon} eyebrow="Field of Images" title="Visual Gallery">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {track.gallery.map((g, i) => (
                  <button
                    key={i}
                    onClick={() => setLightbox(i)}
                    className="group relative aspect-square overflow-hidden rounded-xl border hairline hover:border-gold focus:outline-none focus:ring-2 focus:ring-gold"
                    aria-label={g.caption ?? `Image ${i + 1}`}
                  >
                    <img
                      src={g.url}
                      alt={g.caption ?? ""}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            </SongSection>
          )}

          {track.wallpapers && track.wallpapers.length > 0 && (
            <SongSection icon={Download} eyebrow="Take It With You" title="Wallpapers">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {track.wallpapers.map((w, i) => (
                  <a
                    key={i}
                    href={w.url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block aspect-[3/4] overflow-hidden rounded-xl border hairline hover:border-gold focus:outline-none focus:ring-2 focus:ring-gold"
                    aria-label={`Download ${w.label ?? `wallpaper ${i + 1}`}${w.resolution ? ` (${w.resolution})` : ""}`}
                  >
                    <img
                      src={w.url}
                      alt={w.label ?? `Wallpaper ${i + 1}`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/80 via-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gold-luminous">
                        <span>{w.label ?? `Wallpaper ${i + 1}`}</span>
                        <Download size={12} />
                      </div>
                      {w.resolution && <span className="mt-0.5 text-[9px] text-ivory/70">{w.resolution}</span>}
                    </div>
                  </a>
                ))}
              </div>
            </SongSection>
          )}

          {isPreRelease && (
            <PreSaveCTA
              slug={release.slug}
              title={release.title}
              releaseDate={release.releaseDate}
              preSaveUrl={release.preSaveUrl}
            />
          )}

          <SongComments releaseSlug={release.slug} songSlug={track.slug ?? track.title} />

        </div>

        {/* ASIDE */}
        <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border hairline bg-charcoal p-6">
            <p className="text-xs uppercase tracking-[0.3em] text-gold">From the release</p>
            <Link
              to="/music/$slug"
              params={{ slug: release.slug }}
              className="mt-2 block font-serif text-xl hover:text-gold-luminous"
            >
              {release.title}
            </Link>
            <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
              <div>
                <p className="uppercase tracking-[0.25em] text-muted-foreground">Frequency</p>
                <p className="mt-1 font-serif text-base text-ivory">{release.frequency}</p>
              </div>
              <div>
                <p className="uppercase tracking-[0.25em] text-muted-foreground">Mood</p>
                <p className="mt-1 font-serif text-base text-ivory">{release.mood.join(" · ")}</p>
              </div>
            </div>
          </div>

          {siblings.length > 0 && (
            <div className="rounded-2xl border hairline bg-obsidian/50 p-6">
              <p className="mb-3 text-xs uppercase tracking-[0.3em] text-gold">Other songs on this release</p>
              <ul className="space-y-1">
                {siblings.map((t: TrackListing) => (
                  <li key={t.slug}>
                    <Link
                      to="/music/$slug/song/$song"
                      params={{ slug: release.slug, song: t.slug! }}
                      className="flex min-h-11 items-center justify-between gap-3 rounded-md px-2 py-2 text-sm hover:bg-gold/5"
                    >
                      <span className="font-serif text-ivory/90">{t.title}</span>
                      {t.duration && <span className="text-[10px] text-muted-foreground">{t.duration}</span>}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {suggestions.length > 0 && (
            <div className="rounded-2xl border hairline bg-obsidian/50 p-6">
              <div className="mb-3 flex items-center gap-2">
                <ListMusic size={12} className="text-gold" />
                <p className="text-xs uppercase tracking-[0.3em] text-gold">Playlist suggestions</p>
              </div>
              <ul className="space-y-2">
                {suggestions.map((r: Release) => (
                  <li key={r.slug}>
                    <Link
                      to="/music/$slug"
                      params={{ slug: r.slug }}
                      className="block min-h-11 rounded-md px-2 py-2 hover:bg-gold/5"
                    >
                      <p className="font-serif text-sm text-ivory">{r.title}</p>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        {r.frequency} · {r.mood[0]}
                      </p>
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/playlists"
                    className="mt-2 inline-flex text-xs text-gold hover:text-gold-luminous"
                  >
                    Build your own playlist →
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </aside>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <RelatedRail kind="music" slug={release.slug} heading="Related transmissions" />
      </div>

      {/* Lightbox */}
      {lightbox !== null && track.gallery?.[lightbox] && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-6"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={track.gallery[lightbox].url}
            alt={track.gallery[lightbox].caption ?? ""}
            className="max-h-full max-w-full rounded-lg"
          />
          {track.gallery[lightbox].caption && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-ivory/80">
              {track.gallery[lightbox].caption}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

function SongSection({
  icon: Icon, eyebrow, title, children,
}: { icon: typeof BookOpen; eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-5 flex items-center gap-3">
        <Icon size={16} className="text-gold" aria-hidden />
        <p className="text-xs uppercase tracking-[0.3em] text-gold">{eyebrow}</p>
      </div>
      <h2 className="mb-6 font-serif text-3xl sm:text-4xl">{title}</h2>
      {children}
    </section>
  );
}
